const { Core, Utils, onBlazeLoad } = Global;
const { $toggleDisplay } = Utils;

const ACTIVE_CLASSNAME = 'blaze-active';

function _activeToggle(state) {
    const action = state ? 'add' : 'remove';
    return (el) => {
        el.classList[action](ACTIVE_CLASSNAME);
    }
}

customElements.define('slideshow-inline-blaze', class extends Core {
    elements = {
        $: ['blaze', 'slides-wrapper', ['nav']]
    }

    propTypes = {
        gap: String,
        'slides-per-view': Number,
        'slides-per-view-mobile': Number,
        'slide-min-width': Number,
        'slide-by-row': Boolean,
        'slide-by-item-mobile': Boolean,
        'swipe-on-desktop': Boolean,
        'track-active': Boolean,
        'inert': Boolean
    }

    render() {
        this.navigationControls = this.querySelector('[data-navigation-controls]');
        if(this.navigationControls) {
            Global.SliderNavigation.register(this);
        }

        // if slieshow is interted, it will avtivate only on reset. Handy for dynamically loaded content
        if(this.hasAttribute('inert')) {
            return;
        }

        onBlazeLoad(this._initBlaze.bind(this));
    }

    destroy() {
        Global.SliderNavigation.unregister(this);
        this.navigationUnsubscribe?.();
        this.navigationUnsubscribe = null;
        this.resizeObserver?.disconnect();
        if(this.windowResizeHandler) {
            window.removeEventListener('resize', this.windowResizeHandler);
        }
        if(this.slider) {
            this.slider.destroy();
            this.slider = null;
        }
    }

    replaceSlides(slides) {
        this.$('slides-wrapper').replaceChildren(...slides);
        this.reset();
    }

    reset() {
        if(!this.slider) {
            this.removeAttribute('inert');
            this._initBlaze();
        }
        this.slider.totalSlides = this.slider.slides.length;
        this.slider.refresh();
        this._toggleNavigation();
    }

    _initBlaze() {
        if(!this.isConnected || this.slider || !window.BlazeSlider) {
            // sanity check
            return;
        }

        const slidesPerView = this._getSlidesPerView();
        const slidesPerViewMobile = this.isAutoSized
            ? slidesPerView
            : this.prop('slides-per-view-mobile');

        this._setSlidesPerViewStyles(slidesPerView);

        this.sliderOptions = {
            all: {
                slideGap: this.prop('gap'),
                loop: false,
                slidesToShow: slidesPerView,
                slidesToScroll: this.prop('slide-by-row') ? slidesPerView : 1,
                draggable: this.prop('swipe-on-desktop')
            },
            '(max-width: 991px)': { // match your CSS breakpoint
                slidesToShow: slidesPerViewMobile,
                slidesToScroll: this.prop('slide-by-item-mobile')
                    ? 1
                    : this.prop('slide-by-row')
                    ? Math.max(1, Math.floor(slidesPerViewMobile))
                    : 1,
                draggable: true
            }
        };

        this.slider = new window.BlazeSlider(this.$('blaze'), this.sliderOptions);

        if(this.isAutoSized) {
            this._observeSliderWidth();
        }

        if(this.prop('track-active')) {
            this._handleActiveSlides();
        }
        this._toggleNavigation();
        if(this.navigationControls) {
            Global.SliderNavigation.register(this);
            this.navigationUnsubscribe = this.slider.onSlide(this._updateNavigation.bind(this));
            // Blaze refreshes pages on resize without emitting onSlide. Wrap this
            // instance's refresh so all existing refresh callers update the UI too.
            const slider = this.slider;
            const refresh = slider.refresh.bind(slider);
            slider.refresh = (...args) => {
                if(!this.isConnected || this.slider !== slider) return;
                const result = refresh(...args);
                this._toggleNavigation();
                this._updateNavigation();
                return result;
            };
        }
    }

    setNavigationMode(mode) {
        if(!this.navigationControls) return;
        const blaze = this.$('blaze');
        const previous = blaze.querySelector('.blaze-prev');
        const next = blaze.querySelector('.blaze-next');
        const controls = this.navigationControls;
        const container = controls.parentElement;
        const enhanced = mode !== 'dots';

        // Reuse Blaze's original buttons, including their existing click handlers.
        if(enhanced && previous.parentElement !== controls) {
            controls.append(previous, next);
        } else if(!enhanced && previous.parentElement !== container) {
            container.prepend(next, previous);
        }
        this.dataset.navigationMode = mode;
        if(!enhanced) {
            previous.disabled = false;
            next.disabled = false;
        }
        this._updateNavigation();
    }

    _updateNavigation() {
        if(!this.slider || !this.navigationControls) return;
        const { states, stateIndex, isStatic } = this.slider;
        const state = states[stateIndex];
        const totalItems = this.slider.totalSlides;
        const hasPages = !isStatic && states.length > 1;
        const isProgressMode = this.dataset.navigationMode === 'progress_arrows'
            || this.dataset.navigationMode === 'progress_arrows_white';
        const enhanced = this.dataset.navigationMode !== 'dots';
        const progress = this.querySelector('[data-navigation-progress]');

        this.navigationControls.hidden = !hasPages;
        progress.hidden = !isProgressMode || !hasPages;
        if(isProgressMode) {
            Global.CarouselProgress.update({
                track: progress,
                totalItems,
                visibleItems: this.slider.config.slidesToShow,
                currentIndex: state?.page?.[0] ?? 0
            });
        }
        if(enhanced) {
            this.$('blaze').querySelector('.blaze-prev').disabled = !hasPages || state.prev.stateIndex === stateIndex;
            this.$('blaze').querySelector('.blaze-next').disabled = !hasPages || state.next.stateIndex === stateIndex;
        }
    }

    _getSlidesPerView() {
        if(!this.isAutoSized) {
            return this.prop('slides-per-view');
        }

        const sliderWidth = this.$('blaze').clientWidth;
        const gap = parseFloat(getComputedStyle(this.$('slides-wrapper')).columnGap) || 0;
        const maxSlides = Math.max(1, this.prop('slides-per-view'));
        const slidesThatFit = sliderWidth > 0
            ? Math.max(1, Math.floor((sliderWidth + gap) / (this.prop('slide-min-width') + gap)))
            : maxSlides;

        return Math.min(maxSlides, slidesThatFit);
    }

    _setSlidesPerViewStyles(slidesPerView) {
        if(!this.isAutoSized) {
            return;
        }

        this.style.setProperty('--slides-per-view', slidesPerView);
        this.style.setProperty('--slides-per-view-mobile', slidesPerView);
    }

    _observeSliderWidth() {
        if('ResizeObserver' in window) {
            this.resizeObserver = new window.ResizeObserver(this._updateAutoSize.bind(this));
            this.resizeObserver.observe(this.$('blaze'));
            return;
        }

        this.windowResizeHandler = this._updateAutoSize.bind(this);
        window.addEventListener('resize', this.windowResizeHandler);
    }

    _updateAutoSize() {
        const slidesPerView = this._getSlidesPerView();
        if(slidesPerView === this.sliderOptions.all.slidesToShow) {
            return;
        }

        const slidesToScroll = this.prop('slide-by-row') ? slidesPerView : 1;
        const slidesToScrollMobile = this.prop('slide-by-item-mobile')
            ? 1
            : this.prop('slide-by-row')
                ? Math.max(1, Math.floor(slidesPerView))
                : 1;
        this.sliderOptions.all.slidesToShow = slidesPerView;
        this.sliderOptions.all.slidesToScroll = slidesToScroll;
        this.sliderOptions['(max-width: 991px)'].slidesToShow = slidesPerView;
        this.sliderOptions['(max-width: 991px)'].slidesToScroll = slidesToScrollMobile;
        this._setSlidesPerViewStyles(slidesPerView);
        this.slider.refresh();
        this._toggleNavigation();
    }

    _handleActiveSlides() {
        this.$slides = Array.from(this.slider.slides);
        this._setInitialActiveSlides();
        this.slider.onSlide(this._updateActiveSlides.bind(this));
    }

    _setInitialActiveSlides() {
        const [start, end] = this.slider.states[0].page;
        this.activeSlides = this._findSlidesInRange(start, end);
        this._toggleActiveSlides(true);
    }

    _toggleActiveSlides(state) {
        this.activeSlides.map(_activeToggle(state));
    }

    _findSlidesInRange(start, end) {
        return this.$slides.filter((_, i) => start <= i && end >= i);
    }

    _updateActiveSlides(_, start, end) {
        this._toggleActiveSlides(false);
        this.activeSlides = this._findSlidesInRange(start, end);
        this._toggleActiveSlides(true);
    }

    _toggleNavigation() {
        this.$('nav')?.map(item => $toggleDisplay(item, this.hasOffset));
    }

    get hasOffset() {
        return this.slider.totalSlides > this.slider.config.slidesToShow;
    }

    get isAutoSized() {
        return this.prop('slide-min-width') > 0;
    }
})
